// src/services/tables/myRosterColumns.js - CANONICAL ROSTER COLUMNS FOR MY ROSTER PAGE WITH 3-ROW FORMAT
import React from 'react';
import { ExternalLink, Move, Trash2, ArrowLeftRight } from 'lucide-react';
import { dynastyTheme } from '../colorService';
import { formatContractDisplay } from '../contractUtils';
import { renderDefault, renderFloat1, renderFloat2, renderFloat3, renderAvg } from './DynastyTable';
import InjuryBadge from '../../components/InjuryBadge';
import PlayerLink from '../../components/PlayerLink';
import { isTwoWayPlayer } from '../../utils/playerClassification';

// Small "TWP" pill shown next to a two-way player's name on the reserve view
// (Bible §4.5.9.1 bench rule): a reserve TWP is listed under the hitting bench,
// so the badge signals his dual pitcher/hitter identity.
const TwoWayBadge = () => (
  <span
    className={`${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black} rounded font-bold`}
    style={{ fontSize: '8px', padding: '1px 3px', marginLeft: '4px', lineHeight: 1.2, flexShrink: 0 }}
    title="Two-way player (pitcher + hitter)"
  >
    TWP
  </span>
);

/**
 * Create columns for ACTIVE LINEUP section WITH 3-ROW FORMAT
 * Shows position slots with either a player or empty slot
 * Each slot shows: Season stats (row 1), 14-Day stats (row 2), Accrued stats (row 3)
 * CANONICAL: player.ids.mlb, player.info.*, player.stats.season.*, player.league_context.financial.*
 */
export const createActiveLineupColumns = ({
  statConfigs = [],
  onPlayerClick,
  onMovePlayer,
  onDropPlayer,
  onTradePlayer,
  isViewingOwnTeam = true,
  isPitcher = false,
  leagueStatus
}) => {
  const isOffseason = ['setup', 'draft', 'finalizing'].includes(leagueStatus);
  const getContract = (player) => player.league_context?.financial;
  const columns = [
    // Position Label - Show on middle row (rolling) for vertical centering
    {
      key: 'slotPosition',
      title: 'POS',
      width: 28,
      cellPadding: 'py-0 px-1',
      sortable: false,
      className: (player, rowType, slotInfo) => {
        if (rowType === 'accrued') {
          return 'bg-neutral-800 border-b border-yellow-500/20';
        }
        return 'bg-neutral-800';
      },
      render: (player, rowType, slotInfo) => {
        // Show position on middle row (rolling) for vertical centering
        if (rowType === 'rolling') {
          return (
            <div className="flex items-center justify-center h-full">
              <span className={`${dynastyTheme.classes.text.primary} text-xs font-bold uppercase tracking-wide`}>
                {slotInfo.slotPosition || '?'}
              </span>
            </div>
          );
        }
        return null;
      }
    },
    
    // Player Name with stat type indicator
    {
      key: 'player_name',
      title: 'Player',
      width: 60,
      cellPadding: 'py-0 pl-1 pr-0',
      textAlign: 'text-left',
      sortable: false,
      allowOverflow: true,
      className: (player, rowType, slotInfo) => {
        if (rowType === 'accrued') {
          return 'bg-neutral-800 border-b border-yellow-500/20';
        }
        return 'bg-neutral-800';
      },
      render: (player, rowType, slotInfo) => {
        // DD-STAT-CONSOLIDATION C3: native totals row label (no player identity)
        if (player?.isTotalsRow) {
          return <span className={`${dynastyTheme.classes.text.primary} font-bold text-sm`}>Totals</span>;
        }
        // Season row - show player or "Empty"
        if (rowType === 'season') {
          if (!player) {
            return (
              <span className={`${dynastyTheme.classes.text.neutralDark} text-sm italic`}>
                Empty
              </span>
            );
          }

          return (
            <div className="inline-flex items-center w-full">
              <PlayerLink
                mlbId={player.ids?.mlb}
                name={`${player.info?.first_name || ''} ${player.info?.last_name || ''}`.trim()}
                style={{ fontWeight: 500, fontSize: '0.875rem', color: '#f9fafb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              />
              <InjuryBadge mlbId={player.ids?.mlb} />
              {isTwoWayPlayer(player) && <TwoWayBadge />}
            </div>
          );
        } else {
          // 14-Day and Accrued rows - show stat type label
          return (
            <div className={`text-left text-[10px] font-normal opacity-50 ${
              rowType === 'rolling' 
                ? 'text-yellow-400'
                : 'text-cyan-400'
            }`}>
              {rowType === 'rolling' ? '↗ 14-Day' : '✓ Accrued'}
            </div>
          );
        }
      }
    },
    
    // Team column - Show on middle row (rolling) for vertical centering
    {
      key: 'team',
      title: 'Team',
      width: 32,
      cellPadding: 'py-0 px-1',
      sortable: false,
      className: (player, rowType, slotInfo) => {
        if (rowType === 'accrued') {
          return 'bg-neutral-800 border-b border-yellow-500/20';
        }
        return 'bg-neutral-800';
      },
      render: (player, rowType, slotInfo) => {
        // Show team on middle row (rolling) for vertical centering
        if (rowType === 'rolling' && player) {
          return (
            <div className="flex items-center justify-center h-full">
              <span className={`${dynastyTheme.classes.text.white} text-xs`}>
                {player.info?.mlb_team || '-'}
              </span>
            </div>
          );
        }
        return null;
      }
    }
  ];

  // Add dynamic stat columns based on league settings (with adjusted widths)
  statConfigs.forEach((config) => {
    // Determine column width based on stat type:
    // - G, AB, H, R, SB, RBI: 22 (30% reduction from 32)
    // - AVG, OPS: 42 (30% increase from 32)
    // - Default: 32
    let columnWidth = 32;
    const label = config.label.toUpperCase();
    
    if (['G', 'AB', 'H', 'R', 'SB', 'RBI', 'GS'].includes(label)) {
      columnWidth = 22; // 30% reduction
    } else if (['AVG', 'OPS'].includes(label)) {
      columnWidth = 42; // 30% increase
    }
    
    columns.push({
      key: `stats.${config.field}`,
      title: config.label,
      width: columnWidth,
      cellPadding: 'py-0 px-1',
      sortable: false,
      isStatColumn: true,
      className: (player, rowType, slotInfo) => {
        if (rowType === 'accrued') {
          return 'bg-neutral-800 border-b border-yellow-500/20';
        }
        return 'bg-neutral-800';
      },
      render: (player, rowType, slotInfo) => {
        if (!player) return null;
        
        let statsData;
        if (player?.isTotalsRow) statsData = player.__stats; // DD-STAT-CONSOLIDATION C3: native totals row (season window)
        else if (rowType === 'season') statsData = player.stats?.season;
        else if (rowType === 'rolling') statsData = player.stats?.rolling_14_day;
        else if (rowType === 'accrued') statsData = player.stats?.active_accrued;
        
        if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        
        // CANONICAL: Handle nested path like "batting.home_runs" or "pitching.era"
        let value;
        if (config.field.includes('.')) {
          const parts = config.field.split('.');
          value = statsData;
          for (const part of parts) {
            if (!value) break;
            value = value[part];
          }
        } else {
          // Legacy flat access fallback
          value = statsData[config.field];
        }
        
        if (value === undefined || value === null) {
          return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        }
        
        // Format based on config decimals/prefix (STAT_FIELD_MAP uses these, not config.format)
        let formattedValue;
        if (config.decimals === 3 && config.prefix) {
          // AVG/OPS/OBP/SLG: no leading zero, 3 decimal places (.345)
          formattedValue = renderAvg(value);
        } else if (config.decimals === 2) {
          // ERA/WHIP: 2 decimal places (3.45)
          formattedValue = renderFloat2(value);
        } else if (config.decimals === 1) {
          // IP: 1 decimal place (5.3)
          formattedValue = renderFloat1(value);
        } else if (config.decimals === 3) {
          formattedValue = renderFloat3(value);
        } else {
          formattedValue = renderDefault(value);
        }
        
        // Color based on row type (white for season, yellow for rolling, cyan for accrued)
        const colorClass = rowType === 'season' 
          ? dynastyTheme.classes.text.white
          : rowType === 'rolling' ? 'text-yellow-400' : 'text-cyan-400';
        
        // Font size: normal for season, smaller+italic for rolling/accrued
        const fontClass = rowType === 'season' ? '' : 'text-xs italic';
        
        return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{formattedValue}</span>;
      }
    });
  });

  // Price column - Show on ROLLING row (2nd row) only
  columns.push({
    key: 'price',
    title: 'Price',
    width: 27,
    cellPadding: 'py-0 px-1',
    sortable: false,
    className: (player, rowType, slotInfo) => {
      if (rowType === 'accrued') {
        return 'bg-neutral-800 border-b border-yellow-500/20';
      }
      return 'bg-neutral-800';
    },
    render: (player, rowType, slotInfo) => {
      if (rowType !== 'rolling') return null;
      if (!player) return null;

      const price = getContract(player)?.baseline_price;

      return (
        <div className="flex items-center justify-center h-full">
          <span className={`${dynastyTheme.classes.text.warning} font-semibold text-sm`}>
            ${price}
          </span>
        </div>
      );
    }
  });

  // Salary column - Show on ROLLING row (2nd row) only
  columns.push({
    key: 'salary',
    title: 'Salary',
    width: 27,
    cellPadding: 'py-0 px-1',
    sortable: false,
    className: (player, rowType, slotInfo) => {
      if (rowType === 'accrued') {
        return 'bg-neutral-800 border-b border-yellow-500/20';
      }
      return 'bg-neutral-800';
    },
    render: (player, rowType, slotInfo) => {
      if (rowType !== 'rolling') return null;
      if (!player) return null;

      const salary = getContract(player)?.contract_salary;

      return (
        <div className="flex items-center justify-center h-full">
          <span className={`${dynastyTheme.classes.text.success} font-semibold text-sm`}>
            ${salary}
          </span>
        </div>
      );
    }
  });

  // Contract column - Show on ROLLING row (2nd row) only
  columns.push({
    key: 'contract',
    title: 'Contract',
    width: 34,
    cellPadding: 'py-0 px-1',
    sortable: false,
    className: (player, rowType, slotInfo) => {
      if (rowType === 'accrued') {
        return 'bg-neutral-800 border-b border-yellow-500/20';
      }
      return 'bg-neutral-800';
    },
    render: (player, rowType, slotInfo) => {
      if (rowType !== 'rolling') return null;
      if (!player) return null;

      const contract = getContract(player);
      const display = formatContractDisplay(contract?.contract_type, contract?.contract_years);

      return (
        <div className="flex items-center justify-center h-full">
          <span className={`${display === '\u2014' ? dynastyTheme.classes.text.neutralDark : `${dynastyTheme.classes.text.white} font-semibold`} text-sm`}>
            {display}
          </span>
        </div>
      );
    }
  });

  // Actions column - Show on ROLLING row (2nd row) only (only if viewing own team)
  if (isViewingOwnTeam && onMovePlayer) {
    columns.push({
      key: 'actions',
      title: 'Actions',
      width: 62,
      cellPadding: 'py-0 px-1',
      sortable: false,
      className: (player, rowType, slotInfo) => {
        if (rowType === 'accrued') {
          return 'bg-neutral-800 border-b border-yellow-500/20';
        }
        return 'bg-neutral-800';
      },
      render: (player, rowType, slotInfo) => {
        if (rowType !== 'rolling') return null;
        if (!player) return null;
        
        return (
          <div className="flex items-center justify-center h-full gap-1">
            {/* Move button - opens modal to change position/status */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMovePlayer(player);
              }}
              className="p-1 text-xs rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-all"
              title="Move player"
            >
              <Move className="w-3 h-3" />
            </button>
            
            {/* Drop button */}
            {onDropPlayer && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDropPlayer(player);
                }}
                className="p-1 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
                title="Drop player"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
            
            {/* Trade button */}
            {onTradePlayer && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTradePlayer(player);
                }}
                className="p-1 text-xs rounded bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-all"
                title="Trade player"
              >
                <ArrowLeftRight className="w-3 h-3" />
              </button>
            )}
          </div>
        );
      }
    });
  }

  return columns;
};

/**
 * Create columns for BENCH/DL/MINORS sections WITH 3-ROW FORMAT
 * Shows list of players with season/14-day/accrued stats
 * CANONICAL: player.ids.mlb, player.info.*, player.stats.season.*, player.league_context.financial.*
 */
export const createReservePlayersColumns = ({
  statConfigs = [],
  onPlayerClick,
  onMovePlayer,
  onDropPlayer,
  onTradePlayer,
  isViewingOwnTeam = true,
  sectionType = 'bench', // 'bench', 'dl', 'minors'
  isPitcher = false,
  leagueStatus
}) => {
  const isOffseason = ['setup', 'draft', 'finalizing'].includes(leagueStatus);
  const getContract = (player) => player.league_context?.financial;
  const columns = [
    // Position column
    {
      key: 'position',
      title: 'POS',
      width: 28,  // Narrower width for POS
      cellPadding: 'py-0 px-1',
      sortable: false,
      className: (player, rowType, slotInfo) => {
        if (rowType === 'accrued') {
          return 'bg-neutral-800 border-b border-yellow-500/20';
        }
        return 'bg-neutral-800';
      },
      render: (player, rowType, slotInfo) => {
        // Show position on season row only
        if (rowType === 'season' && player) {
          return (
            <span className={`${dynastyTheme.classes.text.primary} text-xs font-bold uppercase tracking-wide`}>
              {player.info?.position || '?'}
            </span>
          );
        }
        return null;
      }
    },
    
    // Player Name with stat type indicator
    {
      key: 'player_name',
      title: 'Player',
      width: 60,  // Keep at 60px for player names
      sortable: false,
      allowOverflow: true,
      cellPadding: 'py-0 px-0',  // Remove right padding
      className: (player, rowType, slotInfo) => {
        if (rowType === 'accrued') {
          return 'bg-neutral-800 border-b border-yellow-500/20';
        }
        return 'bg-neutral-800';
      },
      render: (player, rowType, slotInfo) => {
        // Season row - show player name
        if (rowType === 'season') {
          return (
            <div className="inline-flex items-center w-full">
              <PlayerLink
                mlbId={player.ids?.mlb}
                name={`${player.info?.first_name || ''} ${player.info?.last_name || ''}`.trim()}
                style={{ fontWeight: 500, fontSize: '0.875rem', color: '#f9fafb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              />
              <InjuryBadge mlbId={player.ids?.mlb} />
              {isTwoWayPlayer(player) && <TwoWayBadge />}
            </div>
          );
        } else {
          // 14-Day and Accrued rows - show stat type label (MATCH ACTIVE LINEUP EXACTLY)
          return (
            <div className={`text-left text-[10px] font-normal opacity-50 ${
              rowType === 'rolling' 
                ? 'text-yellow-400'
                : 'text-cyan-400'
            }`}>
              {rowType === 'rolling' ? '↗ 14-Day' : '✓ Accrued'}
            </div>
          );
        }
      }
    },
    
    // Team column
    {
      key: 'team',
      title: 'Team',
      width: 32,  // Narrower width for Team
      cellPadding: 'py-0 px-1',
      sortable: false,
      className: (player, rowType, slotInfo) => {
      if (rowType === 'accrued') {
      return 'bg-neutral-800 border-b border-yellow-500/20';
      }
      return 'bg-neutral-800';
      },
      render: (player, rowType, slotInfo) => {
        // Show team on season row only
        if (rowType === 'season' && player) {
          return (
            <span className={`${dynastyTheme.classes.text.neutralLight} text-xs`}>
              {player.info?.mlb_team || '-'}
            </span>
          );
        }
        return null;
      }
    }
  ];

  // Add dynamic stat columns based on league settings (with adjusted widths)
  statConfigs.forEach((config) => {
    // Determine column width based on stat type:
    // - G, AB, H, R, SB, RBI: 22 (30% reduction from 32)
    // - AVG, OPS: 42 (30% increase from 32)
    // - Default: 32
    let columnWidth = 32;
    const label = config.label.toUpperCase();
    
    if (['G', 'AB', 'H', 'R', 'SB', 'RBI', 'GS'].includes(label)) {
      columnWidth = 22; // 30% reduction
    } else if (['AVG', 'OPS'].includes(label)) {
      columnWidth = 42; // 30% increase
    }
    
    columns.push({
      key: `stats.${config.field}`,
      title: config.label,
      width: columnWidth,
      cellPadding: 'py-0 px-1',
      sortable: false,
      isStatColumn: true,
      className: (player, rowType, slotInfo) => {
        if (rowType === 'accrued') {
          return 'bg-neutral-800 border-b border-yellow-500/20';
        }
        return 'bg-neutral-800';
      },
      render: (player, rowType, slotInfo) => {
        let statsData;
        if (player?.isTotalsRow) statsData = player.__stats; // DD-STAT-CONSOLIDATION C3: native totals row (season window)
        else if (rowType === 'season') statsData = player.stats?.season;
        else if (rowType === 'rolling') statsData = player.stats?.rolling_14_day;
        else if (rowType === 'accrued') statsData = player.stats?.active_accrued;
        
        if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        
        // CANONICAL: Handle nested path like "batting.home_runs" or "pitching.era"
        let value;
        if (config.field.includes('.')) {
          const parts = config.field.split('.');
          value = statsData;
          for (const part of parts) {
            if (!value) break;
            value = value[part];
          }
        } else {
          // Legacy flat access fallback
          value = statsData[config.field];
        }
        
        if (value === undefined || value === null) {
          return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        }
        
        // Format based on config decimals/prefix (STAT_FIELD_MAP uses these, not config.format)
        let formattedValue;
        if (config.decimals === 3 && config.prefix) {
          // AVG/OPS/OBP/SLG: no leading zero, 3 decimal places (.345)
          formattedValue = renderAvg(value);
        } else if (config.decimals === 2) {
          // ERA/WHIP: 2 decimal places (3.45)
          formattedValue = renderFloat2(value);
        } else if (config.decimals === 1) {
          // IP: 1 decimal place (5.3)
          formattedValue = renderFloat1(value);
        } else if (config.decimals === 3) {
          formattedValue = renderFloat3(value);
        } else {
          formattedValue = renderDefault(value);
        }
        
        // Color based on row type (white for season, yellow for rolling, cyan for accrued)
        const colorClass = rowType === 'season' 
          ? dynastyTheme.classes.text.white
          : rowType === 'rolling' ? 'text-yellow-400' : 'text-cyan-400';
        
        // Font size: normal for season, smaller+italic for rolling/accrued
        const fontClass = rowType === 'season' ? '' : 'text-xs italic';
        
        return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{formattedValue}</span>;
      }
    });
  });

  // Price column - Show on ROLLING row (2nd row) only
  if (sectionType !== 'minors') {
    columns.push({
      key: 'price',
      title: 'Price',
      width: 25,
      cellPadding: 'py-0 px-1',
      sortable: false,
      className: (player, rowType, slotInfo) => {
      if (rowType === 'accrued') {
      return 'bg-neutral-800 border-b border-yellow-500/20';
      }
      return 'bg-neutral-800';
      },
      render: (player, rowType, slotInfo) => {
        if (rowType !== 'rolling') return null;

        const price = getContract(player)?.baseline_price;

        return (
          <div className="flex items-center justify-center h-full">
            <span className={`${dynastyTheme.classes.text.warning} font-semibold text-sm`}>
              ${price}
            </span>
          </div>
        );
      }
    });
  }

  // Salary column - Show on ROLLING row (2nd row) only
  columns.push({
    key: 'salary',
    title: 'Salary',
    width: 27,
    cellPadding: 'py-0 px-1',
    sortable: false,
    className: (player, rowType, slotInfo) => {
      if (rowType === 'accrued') {
        return 'bg-neutral-800 border-b border-yellow-500/20';
      }
      return 'bg-neutral-800';
    },
    render: (player, rowType, slotInfo) => {
      if (rowType !== 'rolling') return null;

      if (sectionType === 'minors') {
        return (
          <div className="flex items-center justify-center h-full">
            <span className={dynastyTheme.classes.text.neutralDark}>-</span>
          </div>
        );
      }

      const salary = getContract(player)?.contract_salary;

      return (
        <div className="flex items-center justify-center h-full">
          <span className={`${dynastyTheme.classes.text.success} font-semibold text-sm`}>
            ${salary}
          </span>
        </div>
      );
    }
  });

  // Contract column - Show on ROLLING row (2nd row) only
  columns.push({
    key: 'contract',
    title: 'Contract',
    width: 34,
    cellPadding: 'py-0 px-1',
    sortable: false,
    className: (player, rowType, slotInfo) => {
      if (rowType === 'accrued') {
        return 'bg-neutral-800 border-b border-yellow-500/20';
      }
      return 'bg-neutral-800';
    },
    render: (player, rowType, slotInfo) => {
      if (rowType !== 'rolling') return null;

      if (sectionType === 'minors') {
        return (
          <div className="flex items-center justify-center h-full">
            <span className={dynastyTheme.classes.text.neutralDark}>-</span>
          </div>
        );
      }

      const contract = getContract(player);
      const display = formatContractDisplay(contract?.contract_type, contract?.contract_years);

      return (
        <div className="flex items-center justify-center h-full">
          <span className={`${display === '\u2014' ? dynastyTheme.classes.text.neutralDark : `${dynastyTheme.classes.text.white} font-semibold`} text-sm`}>
            {display}
          </span>
        </div>
      );
    }
  });

  // Actions column - Show on ROLLING row (2nd row) only (only if viewing own team)
  if (isViewingOwnTeam && onMovePlayer) {
    columns.push({
      key: 'actions',
      title: 'Actions',
      width: 62,
      cellPadding: 'py-0 px-1',
      sortable: false,
      className: (player, rowType, slotInfo) => {
        if (rowType === 'accrued') {
          return 'bg-neutral-800 border-b border-yellow-500/20';
        }
        return 'bg-neutral-800';
      },
      render: (player, rowType, slotInfo) => {
        if (rowType !== 'rolling') return null;
        
        return (
          <div className="flex items-center justify-center h-full gap-1">
            {/* Move button - opens modal for all sections (bench, DL, minors) */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMovePlayer(player);
              }}
              className={`p-1 text-xs rounded transition-all ${
                sectionType === 'bench' ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' :
                'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
              }`}
              title={sectionType === 'bench' ? 'Start player' : sectionType === 'dl' ? 'Activate from DL' : 'Call Up'}
            >
              <Move className="w-3 h-3" />
            </button>
            
            {/* Drop button */}
            {onDropPlayer && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDropPlayer(player);
                }}
                className="p-1 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
                title="Drop player"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
            
            {/* Trade button */}
            {onTradePlayer && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTradePlayer(player);
                }}
                className="p-1 text-xs rounded bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-all"
                title="Trade player"
              >
                <ArrowLeftRight className="w-3 h-3" />
              </button>
            )}
          </div>
        );
      }
    });
  }

  return columns;
};



/**
 * Helper to get stat value from canonical player structure
 */
export const getStatValue = (player, config) => {
  return player?.stats?.season?.[config.field];
};

/**
 * Helper to format stat value based on config
 */
export const formatStatValue = (value, config) => {
  if (value === undefined || value === null) return '-';
  
  if (config.format === 'avg') {
    return renderAvg(value);
  } else if (config.format === 'float1') {
    return renderFloat1(value);
  } else if (config.format === 'float2') {
    return renderFloat2(value);
  } else if (config.format === 'float3') {
    return renderFloat3(value);
  } else {
    return renderDefault(value);
  }
};


